import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Platform, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { Address } from '@/api/subscriptions/types';
import { Card, Input } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { CheckItem } from '@/ui/check-item';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { ArrowRight, Search } from '@/ui/icons';
import { ControlledInputMulti } from '@/ui/input-multi';

const addressSchema = z.object({
  description: z.string(),
  placeId: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string(),
});

export const addLocationFormSchema = z
  .object({
    address: addressSchema,
    pickupInstructions: z.string().min(10, 'Minimum 10 characters required'),
    dropoffInstructions: z.string().min(10, 'Minimum 10 characters required'),
    garageOpenHour: z.string().optional(),
    garageCloseHour: z.string().optional(),
    enableGarageHours: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.enableGarageHours) {
      if (!data.garageOpenHour) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['garageOpenHour'],
          message: 'Garage open hour is required',
        });
      }
      if (!data.garageCloseHour) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['garageCloseHour'],
          message: 'Garage close hour is required',
        });
      }
    }
  });

export type AddLocationFormProps = {
  address: Address;
  pickupInstructions: string;
  dropoffInstructions: string;
  garageOpenHour?: string;
  garageCloseHour?: string;
  enableGarageHours?: boolean;
};

export type AddLocationFormType = z.infer<typeof addLocationFormSchema>;

export default function AddLocationForm({
  onValidChange,
  onChange,
  defaultValues,
}: {
  onValidChange: (isValid: boolean) => void;
  onChange: (values: AddLocationFormProps) => void;
  defaultValues?: Partial<AddLocationFormType>;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const [showGoogleAutoComplete, setShowGoogleAutoComplete] =
    useState<boolean>(false);

  const {
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
    watch,
  } = useForm<AddLocationFormType>({
    defaultValues: defaultValues ?? {},
    resolver: zodResolver(addLocationFormSchema),
  });

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid]);

  useEffect(() => {
    const sub = watch(() => {
      onChange(getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (defaultValues) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [address, enableGarageHours, garageOpenHour, garageCloseHour] =
    useWatch({
      control,
      name: [
        'address',
        'enableGarageHours',
        'garageOpenHour',
        'garageCloseHour',
      ],
    });

  const options = useMemo(() => {
    return Array.from({ length: 24 * 2 }, (_, i) => {
      const hour = Math.floor(i / 2);
      const minute = (i % 2) * 30;
      const pad = (n: number) => n.toString().padStart(2, '0');
      return {
        id: `${pad(hour)}:${pad(minute)}`,
        value: `${pad(hour)}:${pad(minute)}`,
        title:
          hour === 0
            ? `12:${pad(minute)} AM`
            : hour < 12
            ? `${pad(hour)}:${pad(minute)} AM`
            : hour === 12
            ? `12:${pad(minute)} PM`
            : `${pad(hour - 12)}:${pad(minute)} PM`,
      };
    });
  }, []);

  const content = (
    <View style={{ flexGrow: 1, gap: 64 }}>
      <View
        style={{
          flexGrow: 1,
          justifyContent: 'space-between',
          paddingBottom: 24,
        }}
      >
        <View style={{ gap: 24, flexGrow: 1 }}>
          <Alert
            type={AlertType.BRAND}
            title="Guests will receive pickup instructions automatically one hour before their rental begins. Only the approved driver will have access to these instructions at the start of their trip"
          />

          <View
            style={[
              {
                flexGrow: showGoogleAutoComplete ? 1 : 0,
                zIndex: showGoogleAutoComplete ? 3 : 1,
              },
              styles.addressContainer,
            ]}
          >
            {!showGoogleAutoComplete ? (
              <Input
                placeholder={
                  address ? address.description : 'Search and add an address'
                }
                value={address ? address.description : ''}
                onChangeText={() => {}}
                onFocus={() => {
                  setShowGoogleAutoComplete(true);
                }}
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<Search />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
              />
            ) : null}

            {showGoogleAutoComplete ? (
              <GoogleAutocomplete
                onPress={(d: Address) => {
                  setValue('address', {
                    description: d.description,
                    placeId: d.placeId,
                    latitude: d.latitude,
                    longitude: d.longitude,
                    city: d.city,
                    state: d.state,
                    zipcode: d.zipcode,
                  });
                  trigger();

                  setShowGoogleAutoComplete(false);
                }}
                style={{ width: '100%' }}
                autoFocus
              />
            ) : null}
          </View>

          <View
            style={[
              styles.inputsContainer,
              {
                zIndex: 2,
              },
            ]}
          >
            <ControlledInputMulti
              control={control}
              name="pickupInstructions"
              label="Pickup Instructions"
              onChangeText={(s: string) => {
                setValue('pickupInstructions', s);
                trigger();
              }}
              placeholder="Example: “Car is behind the building, use alley on the right”"
              style={{ minHeight: 100 }}
              tooltipText="Provide 1-2 sentences with specific pickup tips."
            />

            <ControlledInputMulti
              control={control}
              name="dropoffInstructions"
              label="Dropoff Instructions"
              onChangeText={(s: string) => {
                setValue('dropoffInstructions', s);
                trigger();
              }}
              placeholder="Example: “Plug in the car, park in the same spot, leave keys inside"
              style={{ minHeight: 100 }}
              tooltipText="Provide 1-2 sentences with specific return steps."
            />

            <Card>
              <View
                style={{
                  padding: 16,
                  borderBottomWidth: enableGarageHours ? 1 : 0,
                  borderBottomColor: theme.colors.card.default.borderColor,
                }}
              >
                <CheckItem
                  text="Garage hours"
                  caption="Set hours when the car can be picked up from this location. Use this for parking garages with limited access hours."
                  onPress={() => {
                    if (enableGarageHours) {
                      setValue('garageOpenHour', undefined);
                      setValue('garageCloseHour', undefined);
                    }

                    setValue('enableGarageHours', !enableGarageHours);
                    trigger();
                  }}
                  active={enableGarageHours}
                  type="checkbox"
                />
              </View>

              {enableGarageHours ? (
                <View
                  style={{
                    padding: 16,
                    gap: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Dropdown
                    mode="single"
                    label="Available From"
                    options={options}
                    placeHolderText="Select Time"
                    values={garageOpenHour ? [garageOpenHour] : []}
                    onSelect={(selected: DropdownOption[]) => {
                      setValue('garageOpenHour', selected[0].value);
                      trigger();
                    }}
                    style={{ flex: 1 }}
                  />

                  <Icon
                    type="naked"
                    icon={<ArrowRight />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                  />

                  <Dropdown
                    mode="single"
                    label="Available Until"
                    options={options}
                    placeHolderText="Select Time"
                    values={garageCloseHour ? [garageCloseHour] : []}
                    onSelect={(selected: DropdownOption[]) => {
                      setValue('garageCloseHour', selected[0].value);
                      trigger();
                    }}
                    style={{ flex: 1 }}
                  />
                </View>
              ) : null}
            </Card>
          </View>
        </View>
      </View>
    </View>
  );

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
      }}
    >
      {Platform.OS === 'web' ? content : content}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  scrollView: {
    flexGrow: 1,
    borderRadius: 16,
  },
  addressContainer: {
    width: {
      md: 700,
    },
  },
  inputsContainer: {
    gap: 32,
    width: {
      md: 700,
    },
  },
}));
